import React, { useState } from "react";
import { toast } from "react-toastify";
import ExcelJS from "exceljs";
import * as XLSX from "xlsx";
import { useBulkLeadUploadMutation } from "../../Services/campign/campignList";
import { Box, LinearProgress } from "@mui/material";

// Utility to get Excel column letters (A, B, ..., Z, AA, AB, ...)
function getExcelColumnLetter(colIndex: number): string {
  let letter = '';
  while (colIndex > 0) {
    let mod = (colIndex - 1) % 26;
    letter = String.fromCharCode(65 + mod) + letter;
    colIndex = Math.floor((colIndex - mod) / 26);
  }
  return letter;
}


interface IProps {
  closeModal: any;
  refetch: any;
  dynamicData: any;
  campaignId: any;
  refetchWorkflow: any;
}

const BulkLeadModel: React.FC<IProps> = ({
  closeModal,
  refetch,
  dynamicData,
  campaignId,
  refetchWorkflow,
}) => {
  const [selectedCsvFiles, setSelectedCsvFiles] = useState<File[]>([]);
  const [fileToDownload, setFileToDownload] = useState<File | null>(null);
  const [failDetails, setFailDetails] = useState<any[]>([]); // Store fail details here
  const [triggerBulkUpload] = useBulkLeadUploadMutation({});
  const [isAssigneeCheck, setIsAssigneeCheck] = useState(false);
  const [progress, setProgress] = React.useState(null);
  const [showingLoader, setShowingLoader] = useState(false);
  const handleCsvFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const files = Array.from(event.target.files);
      setSelectedCsvFiles(files);
      setFileToDownload(files[0]);
    }
  };

  const handleRemoveCsvFile = (index: number) => {
    const newFiles = Array.from(selectedCsvFiles);
    newFiles.splice(index, 1);
    setSelectedCsvFiles(newFiles);
    if (index === 0 && newFiles.length > 0) {
      setFileToDownload(newFiles[0]); // Update the file to be downloaded if the first file was removed
    } else if (index === 0) {
      setFileToDownload(null); // Clear file to download if no files are left
    }
  };

  const handleCsvDownload = async () => {
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet("Sheet 1");

    // Define header row
    const header = [
      "campaign_account_name",
      "campaign_first_name",
      "campaign_last_name",
      "campaign_email",
      "campaign_country_code",
      "campaign_phone_number",
    ];

    if (isAssigneeCheck) {
      const phoneIndex = header.indexOf("campaign_phone_number");
      if (phoneIndex !== -1) {
        header.splice(phoneIndex + 2, 0, "campaign_assignee");
      }
    }

    if (dynamicData && dynamicData.length) {
      dynamicData.forEach((field, index) => {
        const csvKey = field.label.replace(/ /g, "_"); // Unique column namess
        header.push(`campaign_${csvKey}`);
      });
    }

    // const headerUpdates: { index: number; value: string }[] = [];

    const hiddenSheet = workbook.addWorksheet("DropdownOptions", {
      state: "hidden",
    });

    const countryCodes = [
      { code: '+93', iso: 'AF' },
      { code: '+355', iso: 'AL' },
      { code: '+213', iso: 'DZ' },
      { code: '+376', iso: 'AD' },
      { code: '+244', iso: 'AO' },
      { code: '+672', iso: 'AQ' },
      { code: '+54', iso: 'AR' },
      { code: '+374', iso: 'AM' },
      { code: '+297', iso: 'AW' },
      { code: '+61', iso: 'AU' },
      { code: '+43', iso: 'AT' },
      { code: '+994', iso: 'AZ' },
      { code: '+973', iso: 'BH' },
      { code: '+880', iso: 'BD' },
      { code: '+375', iso: 'BY' },
      { code: '+32', iso: 'BE' },
      { code: '+501', iso: 'BZ' },
      { code: '+229', iso: 'BJ' },
      { code: '+975', iso: 'BT' },
      { code: '+591', iso: 'BO' },
      { code: '+387', iso: 'BA' },
      { code: '+267', iso: 'BW' },
      { code: '+55', iso: 'BR' },
      { code: '+673', iso: 'BN' },
      { code: '+359', iso: 'BG' },
      { code: '+226', iso: 'BF' },
      { code: '+257', iso: 'BI' },
      { code: '+855', iso: 'KH' },
      { code: '+237', iso: 'CM' },
      { code: '+1', iso: 'CA' },
      { code: '+238', iso: 'CV' },
      { code: '+236', iso: 'CF' },
      { code: '+235', iso: 'TD' },
      { code: '+56', iso: 'CL' },
      { code: '+86', iso: 'CN' },
      { code: '+57', iso: 'CO' },
      { code: '+269', iso: 'KM' },
      { code: '+682', iso: 'CK' },
      { code: '+506', iso: 'CR' },
      { code: '+385', iso: 'HR' },
      { code: '+53', iso: 'CU' },
      { code: '+599', iso: 'CW' },
      { code: '+357', iso: 'CY' },
      { code: '+420', iso: 'CZ' },
      { code: '+243', iso: 'CD' },
      { code: '+45', iso: 'DK' },
      { code: '+253', iso: 'DJ' },
      { code: '+670', iso: 'TL' },
      { code: '+593', iso: 'EC' },
      { code: '+20', iso: 'EG' },
      { code: '+503', iso: 'SV' },
      { code: '+240', iso: 'GQ' },
      { code: '+291', iso: 'ER' },
      { code: '+372', iso: 'EE' },
      { code: '+251', iso: 'ET' },
      { code: '+500', iso: 'FK' },
      { code: '+298', iso: 'FO' },
      { code: '+679', iso: 'FJ' },
      { code: '+358', iso: 'FI' },
      { code: '+33', iso: 'FR' },
      { code: '+689', iso: 'PF' },
      { code: '+241', iso: 'GA' },
      { code: '+220', iso: 'GM' },
      { code: '+995', iso: 'GE' },
      { code: '+49', iso: 'DE' },
      { code: '+233', iso: 'GH' },
      { code: '+350', iso: 'GI' },
      { code: '+30', iso: 'GR' },
      { code: '+299', iso: 'GL' },
      { code: '+502', iso: 'GT' },
      { code: '+224', iso: 'GN' },
      { code: '+245', iso: 'GW' },
      { code: '+592', iso: 'GY' },
      { code: '+509', iso: 'HT' },
      { code: '+504', iso: 'HN' },
      { code: '+852', iso: 'HK' },
      { code: '+36', iso: 'HU' },
      { code: '+354', iso: 'IS' },
      { code: '+91', iso: 'IN' },
      { code: '+62', iso: 'ID' },
      { code: '+98', iso: 'IR' },
      { code: '+964', iso: 'IQ' },
      { code: '+353', iso: 'IE' },
      { code: '+972', iso: 'IL' },
      { code: '+39', iso: 'IT' },
      { code: '+225', iso: 'CI' },
      { code: '+81', iso: 'JP' },
      { code: '+962', iso: 'JO' },
      { code: '+7', iso: 'KZ' },
      { code: '+254', iso: 'KE' },
      { code: '+686', iso: 'KI' },
      { code: '+383', iso: 'XK' },
      { code: '+965', iso: 'KW' },
      { code: '+996', iso: 'KG' },
      { code: '+856', iso: 'LA' },
      { code: '+371', iso: 'LV' },
      { code: '+961', iso: 'LB' },
      { code: '+266', iso: 'LS' },
      { code: '+231', iso: 'LR' },
      { code: '+218', iso: 'LY' },
      { code: '+423', iso: 'LI' },
      { code: '+370', iso: 'LT' },
      { code: '+352', iso: 'LU' },
      { code: '+853', iso: 'MO' },
      { code: '+389', iso: 'MK' },
      { code: '+261', iso: 'MG' },
      { code: '+265', iso: 'MW' },
      { code: '+60', iso: 'MY' },
      { code: '+960', iso: 'MV' },
      { code: '+223', iso: 'ML' },
      { code: '+356', iso: 'MT' },
      { code: '+692', iso: 'MH' },
      { code: '+222', iso: 'MR' },
      { code: '+230', iso: 'MU' },
      { code: '+52', iso: 'MX' },
      { code: '+691', iso: 'FM' },
      { code: '+373', iso: 'MD' },
      { code: '+377', iso: 'MC' },
      { code: '+976', iso: 'MN' },
      { code: '+382', iso: 'ME' },
      { code: '+212', iso: 'MA' },
      { code: '+258', iso: 'MZ' },
      { code: '+95', iso: 'MM' },
      { code: '+264', iso: 'NA' },
      { code: '+674', iso: 'NR' },
      { code: '+977', iso: 'NP' },
      { code: '+31', iso: 'NL' },
      { code: '+64', iso: 'NZ' },
      { code: '+234', iso: 'NG' },
      { code: '+47', iso: 'NO' },
      { code: '+968', iso: 'OM' },
      { code: '+92', iso: 'PK' },
      { code: '+63', iso: 'PH' },
      { code: '+48', iso: 'PL' },
      { code: '+351', iso: 'PT' },
      { code: '+974', iso: 'QA' },
      { code: '+40', iso: 'RO' },
      { code: '+7', iso: 'RU' },
      { code: '+966', iso: 'SA' },
      { code: '+65', iso: 'SG' },
      { code: '+27', iso: 'ZA' },
      { code: '+34', iso: 'ES' },
      { code: '+94', iso: 'LK' },
      { code: '+46', iso: 'SE' },
      { code: '+41', iso: 'CH' },
      { code: '+90', iso: 'TR' },
      { code: '+971', iso: 'AE' },
      { code: '+44', iso: 'GB' },
      { code: '+1', iso: 'US' },
      { code: '+84', iso: 'VN' },
      { code: '+967', iso: 'YE' },
      { code: '+263', iso: 'ZW' }
    ];


    countryCodes.forEach((country, index) => {
      hiddenSheet.getCell(`A${index + 1}`).value = `${country.iso} (${country.code})`;
    });

    // dynamicData.forEach((field) => {
    //   if (field.field_type === "tel-phone") {
    //     const fieldKey = `campaign_${field.label.replace(/ /g, "_")}`;
    //     const fieldIndex = header.indexOf(fieldKey);

    //     if (fieldIndex !== -1) {
    //       headerUpdates.push({
    //         index: fieldIndex,
    //         value: `${fieldKey}_country_code`,
    //       });
    //     }
    //   }
    // });

    // headerUpdates.sort((a, b) => b.index - a.index);
    // headerUpdates.forEach(h => header.splice(h.index, 0, h.value));

    ws.addRow(header);

    // Ensure 100 rows exist to avoid "A Cell needs a Row" error
    for (let i = 2; i <= 100; i++) {
      ws.addRow([]);
    }

    // Set column widths
    ws.columns.forEach((col) => {
      col.width = 18;
    });

    // Set phone number column to text format to prevent scientific notation
    const phoneIndex = header.indexOf("campaign_phone_number");
    if (phoneIndex !== -1) {
      const phoneColumn = ws.getColumn(phoneIndex + 1);
      phoneColumn.numFmt = "@"; // Text format
    }

    // Apply styles to header row
    const headerRow = ws.getRow(1);
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFADD8E6" },
    };


    // Add data validation for country code column
    const countryCodeIndex = header.indexOf("campaign_country_code");
    if (countryCodeIndex !== -1) {
      const columnLetter = getExcelColumnLetter(countryCodeIndex + 1);

      // Style the header
      ws.getCell(1, countryCodeIndex + 1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFF0000" }, // Red background
      };
      // Add dropdown validation for country code column
      for (let r = 2; r <= 100; r++) {
        ws.getCell(`${columnLetter}${r}`).dataValidation = {
          type: "list",
          allowBlank: false,
          showErrorMessage: true,
          errorTitle: "Invalid Country Code",
          error: "Please select a valid country code",
          formulae: [`DropdownOptions!$A$1:$A$${countryCodes.length}`],
        };
      }
    }

    // Style required fields in red
    const requiredHeaders = [
      'campaign_country_code',
      'campaign_phone_number',
    ];

    requiredHeaders.forEach((headerName) => {
      const colIndex = header.indexOf(headerName);
      if (colIndex !== -1) {
        ws.getCell(1, colIndex + 1).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFF0000" }, // Red color for required headers
        };
      }
    });

    // Highlight required dynamic fields
    dynamicData.forEach((field, index) => {
      const columnIndex = header.length - dynamicData.length + index + 1; // Adjust for the current dynamic field
      // const columnIndex =
      //   header.indexOf(`campaign_${field.label.replace(/ /g, "_")}`) + 1;
      const columnLetter = getExcelColumnLetter(columnIndex);

      // if (field.field_type === 'tel-phone') {
      //   // Skip processing tel-phone fields here as we've already handled them
      //   return;
      if (field.field_type === "check-box") {
        const checkboxOptions = field.multiple_choices
          .map((choice) => choice.choice)
          .join(",");

        // Add dropdown validation for checkboxes
        for (let i = 2; i <= 100; i++) {
          ws.getCell(`${columnLetter}${i}`).dataValidation = {
            type: "list",
            allowBlank: false,
            formulae: [`"${checkboxOptions}"`],
          };
        }
      } else if (field.field_type === "drop-down") {
        const fieldOptions = field?.choices.map((option: any) => option.choice);

        // Add dropdown validation for drop-downs
        for (let i = 2; i <= 100; i++) {
          ws.getCell(`${columnLetter}${i}`).dataValidation = {
            type: "list",
            allowBlank: false,
            formulae: [`"${fieldOptions}"`],
          };
        }
      }
    });

    if (dynamicData && dynamicData.length) {
      dynamicData.forEach((field, index) => {
        if (field.is_required) {
          const columnIndex = header.length - dynamicData.length + index + 1; // Adjust for the current dynamic field
          // const columnIndex =
          //   header.indexOf(`campaign_${field.label.replace(/ /g, "_")}`) + 1;
          const columnLetter = getExcelColumnLetter(columnIndex);
          ws.getCell(`${columnLetter}1`).fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFF0000" }, // Red color for required dynamic fields
          };
        }
      });
    }

    // Generate Excel file
    const excelBlob = await workbook.xlsx.writeBuffer();
    const excelUrl = URL.createObjectURL(
      new Blob([excelBlob], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      })
    );

    // Create download link
    const link = document.createElement("a");
    link.href = excelUrl;
    link.download = "campaign_template.xlsx";
    document.body.appendChild(link);
    link.click();

    // Cleanup
    URL.revokeObjectURL(excelUrl);
    document.body.removeChild(link);
  };

  const handleDownloadFailDetails = async () => {
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet("Failed Uploads");

    // Prepare header for static fields
    let header = [
      "campaign_account_name",
      "campaign_first_name",
      "campaign_last_name",
      "campaign_email",
      "campaign_country_code",
      "campaign_phone_number",
    ];

    // Dynamically add dynamic field headers
    if (dynamicData && dynamicData.length) {
      dynamicData.forEach((field) => {
        const csvKey = field.label.replace(/ /g, "_"); // Replace spaces with underscores
        header.push(`campaign_${csvKey}`); // Dynamic field label in header
      });
    }

    // Errors column is added at the last
    header.push("Errors");

    const hiddenSheet = workbook.addWorksheet("DropdownOptions", {
      state: "hidden",
    });

    const countryCodes = [
      { code: '+93', iso: 'AF' },
      { code: '+355', iso: 'AL' },
      { code: '+213', iso: 'DZ' },
      { code: '+376', iso: 'AD' },
      { code: '+244', iso: 'AO' },
      { code: '+672', iso: 'AQ' },
      { code: '+54', iso: 'AR' },
      { code: '+374', iso: 'AM' },
      { code: '+297', iso: 'AW' },
      { code: '+61', iso: 'AU' },
      { code: '+43', iso: 'AT' },
      { code: '+994', iso: 'AZ' },
      { code: '+973', iso: 'BH' },
      { code: '+880', iso: 'BD' },
      { code: '+375', iso: 'BY' },
      { code: '+32', iso: 'BE' },
      { code: '+501', iso: 'BZ' },
      { code: '+229', iso: 'BJ' },
      { code: '+975', iso: 'BT' },
      { code: '+591', iso: 'BO' },
      { code: '+387', iso: 'BA' },
      { code: '+267', iso: 'BW' },
      { code: '+55', iso: 'BR' },
      { code: '+673', iso: 'BN' },
      { code: '+359', iso: 'BG' },
      { code: '+226', iso: 'BF' },
      { code: '+257', iso: 'BI' },
      { code: '+855', iso: 'KH' },
      { code: '+237', iso: 'CM' },
      { code: '+1', iso: 'CA' },
      { code: '+238', iso: 'CV' },
      { code: '+236', iso: 'CF' },
      { code: '+235', iso: 'TD' },
      { code: '+56', iso: 'CL' },
      { code: '+86', iso: 'CN' },
      { code: '+57', iso: 'CO' },
      { code: '+269', iso: 'KM' },
      { code: '+682', iso: 'CK' },
      { code: '+506', iso: 'CR' },
      { code: '+385', iso: 'HR' },
      { code: '+53', iso: 'CU' },
      { code: '+599', iso: 'CW' },
      { code: '+357', iso: 'CY' },
      { code: '+420', iso: 'CZ' },
      { code: '+243', iso: 'CD' },
      { code: '+45', iso: 'DK' },
      { code: '+253', iso: 'DJ' },
      { code: '+670', iso: 'TL' },
      { code: '+593', iso: 'EC' },
      { code: '+20', iso: 'EG' },
      { code: '+503', iso: 'SV' },
      { code: '+240', iso: 'GQ' },
      { code: '+291', iso: 'ER' },
      { code: '+372', iso: 'EE' },
      { code: '+251', iso: 'ET' },
      { code: '+500', iso: 'FK' },
      { code: '+298', iso: 'FO' },
      { code: '+679', iso: 'FJ' },
      { code: '+358', iso: 'FI' },
      { code: '+33', iso: 'FR' },
      { code: '+689', iso: 'PF' },
      { code: '+241', iso: 'GA' },
      { code: '+220', iso: 'GM' },
      { code: '+995', iso: 'GE' },
      { code: '+49', iso: 'DE' },
      { code: '+233', iso: 'GH' },
      { code: '+350', iso: 'GI' },
      { code: '+30', iso: 'GR' },
      { code: '+299', iso: 'GL' },
      { code: '+502', iso: 'GT' },
      { code: '+224', iso: 'GN' },
      { code: '+245', iso: 'GW' },
      { code: '+592', iso: 'GY' },
      { code: '+509', iso: 'HT' },
      { code: '+504', iso: 'HN' },
      { code: '+852', iso: 'HK' },
      { code: '+36', iso: 'HU' },
      { code: '+354', iso: 'IS' },
      { code: '+91', iso: 'IN' },
      { code: '+62', iso: 'ID' },
      { code: '+98', iso: 'IR' },
      { code: '+964', iso: 'IQ' },
      { code: '+353', iso: 'IE' },
      { code: '+972', iso: 'IL' },
      { code: '+39', iso: 'IT' },
      { code: '+225', iso: 'CI' },
      { code: '+81', iso: 'JP' },
      { code: '+962', iso: 'JO' },
      { code: '+7', iso: 'KZ' },
      { code: '+254', iso: 'KE' },
      { code: '+686', iso: 'KI' },
      { code: '+383', iso: 'XK' },
      { code: '+965', iso: 'KW' },
      { code: '+996', iso: 'KG' },
      { code: '+856', iso: 'LA' },
      { code: '+371', iso: 'LV' },
      { code: '+961', iso: 'LB' },
      { code: '+266', iso: 'LS' },
      { code: '+231', iso: 'LR' },
      { code: '+218', iso: 'LY' },
      { code: '+423', iso: 'LI' },
      { code: '+370', iso: 'LT' },
      { code: '+352', iso: 'LU' },
      { code: '+853', iso: 'MO' },
      { code: '+389', iso: 'MK' },
      { code: '+261', iso: 'MG' },
      { code: '+265', iso: 'MW' },
      { code: '+60', iso: 'MY' },
      { code: '+960', iso: 'MV' },
      { code: '+223', iso: 'ML' },
      { code: '+356', iso: 'MT' },
      { code: '+692', iso: 'MH' },
      { code: '+222', iso: 'MR' },
      { code: '+230', iso: 'MU' },
      { code: '+52', iso: 'MX' },
      { code: '+691', iso: 'FM' },
      { code: '+373', iso: 'MD' },
      { code: '+377', iso: 'MC' },
      { code: '+976', iso: 'MN' },
      { code: '+382', iso: 'ME' },
      { code: '+212', iso: 'MA' },
      { code: '+258', iso: 'MZ' },
      { code: '+95', iso: 'MM' },
      { code: '+264', iso: 'NA' },
      { code: '+674', iso: 'NR' },
      { code: '+977', iso: 'NP' },
      { code: '+31', iso: 'NL' },
      { code: '+64', iso: 'NZ' },
      { code: '+234', iso: 'NG' },
      { code: '+47', iso: 'NO' },
      { code: '+968', iso: 'OM' },
      { code: '+92', iso: 'PK' },
      { code: '+63', iso: 'PH' },
      { code: '+48', iso: 'PL' },
      { code: '+351', iso: 'PT' },
      { code: '+974', iso: 'QA' },
      { code: '+40', iso: 'RO' },
      { code: '+7', iso: 'RU' },
      { code: '+966', iso: 'SA' },
      { code: '+65', iso: 'SG' },
      { code: '+27', iso: 'ZA' },
      { code: '+34', iso: 'ES' },
      { code: '+94', iso: 'LK' },
      { code: '+46', iso: 'SE' },
      { code: '+41', iso: 'CH' },
      { code: '+90', iso: 'TR' },
      { code: '+971', iso: 'AE' },
      { code: '+44', iso: 'GB' },
      { code: '+1', iso: 'US' },
      { code: '+84', iso: 'VN' },
      { code: '+967', iso: 'YE' },
      { code: '+263', iso: 'ZW' }
    ];


    countryCodes.forEach((country, index) => {
      hiddenSheet.getCell(`A${index + 1}`).value = `${country.iso} (${country.code})`;
    });

    // Add headers to the worksheet
    ws.addRow(header);

    // Set column widths
    ws.columns.map((col) => (col.width = 18));

    // Set phone number column to text format to prevent scientific notation
    const phoneIndex = header.indexOf("campaign_phone_number");
    if (phoneIndex !== -1) {
      const phoneColumn = ws.getColumn(phoneIndex + 1);
      phoneColumn.numFmt = "@"; // Text format
    }

    // Iterate through failDetails to populate rows
    failDetails.forEach((fail) => {
      const {
        account_name,
        first_name,
        last_name,
        email,
        phone_number,
        dynamic_fields = {},
        error,
      } = fail;

      const countryCode = dynamic_fields.country_code;
      // Prepare dynamic fields data in the same structure as CSV
      const dynamicFieldValues = dynamicData.map((field) => {
        const fieldKey = field.label.replace(/ /g, "_");
        return dynamic_fields[fieldKey] || ""; // Return 'N/A' if value is missing
      });

      // Convert errors into string for easier display in Excel
      const errorString = Array.isArray(error)
        ? error.join(", ")
        : JSON.stringify(error);

      ws.addRow([
        account_name,
        first_name,
        last_name,
        email,
        countryCode,
        phone_number,

        ...dynamicFieldValues, // Spread dynamic field values here
        errorString, // Errors at the end
      ]);
    });

      // Apply styles to all cells
      ws.eachRow((row) => {
        row.eachCell((cell) => {
          cell.font = {
            name: "Inter",
            size: 8,
          };
          cell.alignment = {
            horizontal: "center",
          };
        });
      });

    // Apply specific red fill style to headers
    const redHeaders = [
      "campaign_phone_number",
      "campaign_country_code",
    ];
    redHeaders.forEach((headerName) => {
      const colIndex = header.indexOf(headerName) + 1; // Find column index for each red header
      ws.getCell(1, colIndex).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFF0000" }, // Red color for specific headers
      };
    });

    // Apply red fill to required dynamic field headers
    if (dynamicData && dynamicData.length) {
      dynamicData.forEach((field, index) => {
        if (field.is_required) {
          const columnIndex =
            header.indexOf(`campaign_${field.label.replace(/ /g, "_")}`) + 1;
          ws.getCell(1, columnIndex).fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFF0000" }, // Red color for required dynamic fields
          };
        }
      });
    }


    // Add data validation for country code column
    const countryCodeIndex = header.indexOf("campaign_country_code");
    if (countryCodeIndex !== -1) {
      const columnLetter = getExcelColumnLetter(countryCodeIndex + 1);

      // Style the header
      ws.getCell(1, countryCodeIndex + 1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFF0000" }, // Red background
      };
      // Add dropdown validation for country code column
      for (let r = 2; r <= 100; r++) {
        ws.getCell(`${columnLetter}${r}`).dataValidation = {
          type: "list",
          allowBlank: false,
          showErrorMessage: true,
          errorTitle: "Invalid Country Code",
          error: "Please select a valid country code",
          formulae: [`DropdownOptions!$A$1:$A$${countryCodes.length}`],
        };
      }
    }


    // Create the Excel file and initiate the download
    const excelBlob = await workbook.xlsx.writeBuffer();
    const excelUrl = URL.createObjectURL(
      new Blob([excelBlob], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      })
    );

    const link = document.createElement("a");
    link.href = excelUrl;
    link.download = "failed_uploads.xlsx";
    document.body.appendChild(link);
    link.click();

    // Clean up temporary URLs and DOM elements
    URL.revokeObjectURL(excelUrl);
    document.body.removeChild(link);
    closeModal();
  };

  const cleanCsvData = (csvContent: string, percentage: number) => {
    const rows = csvContent.split("\n").map((row) => row.split(","));
    const cleanedRows = rows.filter((row) =>
      row.some((cell) => cell.trim() !== "")
    );
    const [headers, ...dataRows] = cleanedRows;

    const totalRows = Math.ceil((dataRows.length * percentage) / 100);
    const limitedRows = dataRows.slice(0, totalRows);

    const cleanedData = limitedRows.map((row) =>
      headers.reduce((acc, header, index) => {
        acc[header.trim()] = row[index]?.trim() || "";
        return acc;
      }, {})
    );

    return cleanedData;
  };

  const createCsvContent = (data: any[]) => {
    if (data.length === 0) return "";
    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
      headers.map((header) => row[header]).join(",")
    );
    return [headers.join(","), ...rows].join("\n");
  };

  React.useEffect(() => {
    const timer = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress === 100) {
          return 0;
        }
        return oldProgress;
      });
    }, 500);

    return () => {
      clearInterval(timer);
    };
  }, []);


  const toBase64UTF8 = (str: string) =>
    btoa(
      encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) =>
        String.fromCharCode(parseInt(p1, 16))
      )
    );

  const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (selectedCsvFiles.length > 0) {
      setShowingLoader(true);

      const file = selectedCsvFiles[0];
      const fileExtension = file.name.split(".").pop()?.toLowerCase();

      const reader = new FileReader();

      reader.onload = async (e) => {
        let csvContent: string | undefined;

        if (e.target?.result) {
          if (fileExtension === "csv") {
            csvContent = e.target.result as string;
          } else if (fileExtension === "xlsx") {
            const binaryStr = e.target.result as string;
            const workbook = XLSX.read(binaryStr, { type: "binary" });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            csvContent = XLSX.utils.sheet_to_csv(worksheet);
          }

          if (csvContent) {
            const cleanedData = cleanCsvData(csvContent, 100);
            try {
              const csvContentFull = createCsvContent(cleanedData);
              const requestObj = {
                campaign: campaignId,
                file_name: file.name,
                csv_file: `data:text/csv;base64,${toBase64UTF8(csvContentFull)}`,

              };
              const response = await triggerBulkUpload({ body: requestObj });

              setSelectedCsvFiles([]);
              refetch();

              if (response?.data?.fail_details?.length > 0) {
                setFailDetails(response.data.fail_details);
                toast.warning("Some data failed to upload");
              } else if (response?.data?.success_count) {
                toast.success("File uploaded successfully");
              } else {
                toast.error("No data found");
              }
              setShowingLoader(false);
            } catch (error) {
              setShowingLoader(false);
              toast.error("Upload failed. Please try again.");
            }
          }
        }
      };

      if (fileExtension === "csv") {
        reader.readAsText(file);
      } else if (fileExtension === "xlsx") {
        reader.readAsBinaryString(file);
      } else {
        toast.error(
          "Unsupported file format. Please upload a CSV or XLSX file."
        );
      }
    }
  };

  console.log(progress, "progresssss");
  const checkboxStyle: any = {
    width: "20px",
    height: "20px",
    border: "1px solid #6366f1",
    backgroundColor: isAssigneeCheck ? "#6366f1" : "transparent",
    position: "relative",
    cursor: "pointer",
    display: "inline-block",
    appearance: "none", // Removes default styling
    WebkitAppearance: "none", // For WebKit browsers
  };

  const checkmarkStyle: any = {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%) rotate(45deg)",
    width: "5px",
    height: "10px",
    border: "solid white",
    borderWidth: "0 2px 2px 0",
    display: isAssigneeCheck ? "block" : "none", // Only show when checked
  };

  return (
    <form onSubmit={handleFormSubmit}>
      <div className="mb-4">
        <div className="d-flex justify-content-evenly mt-2 mb-8 mx-1">
          {/* CSV Upload Button */}
          <label
            htmlFor="upload-csv-button"
            className="form-control d-flex align-items-center justify-content-center bg-secondary fw-bold mx-2 px-3 py-2 rounded mw-150px"
            style={{ cursor: "pointer" }}
          >
            <i className="bi bi-file-earmark-spreadsheet text-dark fs-4 me-2"></i>
            CSV Upload
          </label>
          <input
            type="file"
            id="upload-csv-button"
            accept=".csv, .xlsx"
            style={{ display: "none" }}
            onChange={handleCsvFileChange}
          />

          {/* CSV Download Button */}
          <button
            type="button"
            className="form-control d-flex align-items-center justify-content-center bg-secondary fw-bold mx-2 px-3 py-2 rounded mw-150px"
            onClick={handleCsvDownload}
            style={{ cursor: "pointer" }}
          >
            <i className="bi bi-download text-dark fs-4 me-2"></i>
            CSV Download
          </button>
        </div>
        <div
          className="d-flex justify-content-start mt-2 mb-8 mx-4"
          style={{ paddingLeft: "26px" }}
        >
          <div
            style={checkboxStyle}
            onClick={() => setIsAssigneeCheck(!isAssigneeCheck)} // Handle toggle
          >
            <div style={checkmarkStyle}></div>
          </div>
          <label htmlFor="show-fail-details" className="ms-2">
            Assign to user
          </label>
        </div>

        {showingLoader ? (
          <Box sx={{ width: "100%" }}>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        ) : (
          ""
        )}
        <div className="mt-3 mb-3" style={{ paddingLeft: "38px" }}>
          <label className="fs-6 fw-bolder mb-2">Important Instructions:</label>
          <br />
          <label className="text-danger">
            &bull; Please upload a valid CSV or XLSX file format only.
          </label>
          <br />
          <label className="text-danger">
            &bull; Headers highlighted in red are mandatory fields.
          </label>
        </div>

        <div>
          {failDetails?.length > 0 && (
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <button
                type="button"
                className="form-control d-flex align-items-center justify-content-center bg-secondary fw-bold mx-6 px-3 py-2 rounded mw-150px"
                onClick={handleDownloadFailDetails}
                style={{ cursor: "pointer" }}
              >
                <i className="bi bi-download text-dark fs-4 me-2"></i>
                Error Data
              </button>
            </div>
          )}
        </div>

        {/* Display selected CSV files */}
        {selectedCsvFiles?.length > 0 && (
          <div className="mb-4" style={{ paddingLeft: "35px" }}>
            <h5>Selected CSV file:</h5>
            <ul className="list-group mt-3">
              {selectedCsvFiles.map((file, index) => (
                <li
                  key={index}
                  className="list-group-item d-flex justify-content-between align-items-center"
                >
                  <span>{file.name}</span>
                  <button
                    type="button"
                    className="btn btn-link text-danger p-0"
                    onClick={() => handleRemoveCsvFile(index)}
                  >
                    <i className="bi bi-trash text-danger fs-5"></i>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <div
        className="d-flex justify-content-end mb-4 pt-2"
        style={{ marginTop: "12%" }}
      >
        <button
          type="button"
          className="btn btn-sm btn-white  me-2"
          onClick={closeModal}
        >
          Cancel
        </button>
        <button type="submit" className="btn btn-sm btn-primary">
          Submit
        </button>
      </div>
    </form>
  );
};

export default BulkLeadModel;
